import { Clock, Phone } from "lucide-react";
import { SiWhatsapp } from "react-icons/si";
import { fmtDate } from "@/lib/format";
import { thanksLine } from "@/components/BuyerForm";
import { waDigits } from "@/components/portal/DocCards";

const DEAL = {
  venda: { goal: "pôr o seu imóvel à venda", of: "da compra e venda", ctx: "desta compra e venda" },
  arrendamento: { goal: "arrendar o seu imóvel", of: "do arrendamento", ctx: "deste arrendamento" },
  trespasse: { goal: "o trespasse", of: "do trespasse", ctx: "deste trespasse" },
};
export const deal = (n) => DEAL[n] || DEAL.venda;

export function ConsultantRow({ c }) {
  const round = "w-10 h-10 rounded-full grid place-items-center shrink-0 transition-transform hover:scale-105";
  return (
    <div className="flex items-center gap-3 pb-4 mb-4 border-b border-white/15" data-testid="portal-consultant">
      {c.photo
        ? <img src={c.photo} alt={c.name} className="w-12 h-12 rounded-full object-cover border-2 border-white/60 shrink-0" data-testid="portal-consultant-photo" />
        : <span className="w-12 h-12 rounded-full bg-white/15 grid place-items-center text-lg font-semibold shrink-0">{c.name.slice(0, 1)}</span>}
      <div className="leading-tight flex-1 min-w-0 overflow-hidden">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.04em] sm:tracking-[0.14em] opacity-70 leading-tight">O seu consultor</div>
        <div className="text-[15px] sm:text-[17px] font-semibold break-words" data-testid="portal-consultant-name">{c.name}</div>
      </div>
      {c.phone && (
        <div className="flex gap-2 shrink-0">
          <a href={`https://wa.me/${waDigits(c.phone)}`} target="_blank" rel="noreferrer" aria-label="WhatsApp" className={`${round} bg-[#25D366] text-white`} data-testid="portal-consultant-wa"><SiWhatsapp size={20} /></a>
          <a href={`tel:${c.phone.replace(/\s/g, "")}`} aria-label="Ligar" className={`${round} bg-white/15 text-white`} data-testid="portal-consultant-call"><Phone size={17} /></a>
        </div>
      )}
    </div>
  );
}

// Verde a 100 %; abaixo, a cor da agência (se for vermelha, branco: progresso nunca a vermelho).
const reddish = (hex) => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec((hex || "").trim());
  if (!m) return false;
  const [r, g, b] = m.slice(1).map((x) => parseInt(x, 16));
  return r > 120 && r > g * 1.8 && r > b * 1.8;
};

export function Progress({ sent, total, accent, verifying = 0 }) {
  const done = total > 0 && sent >= total;
  const fill = done ? "#16a34a" : reddish(accent) ? "var(--on-dark)" : "var(--accent)";
  return (
    <div className="mt-4">
      <div className="h-1.5 rounded-full bg-white/15 overflow-hidden" data-testid="portal-progress">
        <div className="h-full transition-[width] duration-500" data-done={done ? "yes" : "no"} style={{ width: `${total ? (100 * sent) / total : 0}%`, backgroundColor: fill }} />
      </div>
      <div className="text-xs opacity-70 mt-1.5" data-testid="portal-progress-label">{sent} de {total} enviados{verifying > 0 ? ` · ${verifying} em verificação` : ""}</div>
    </div>
  );
}

// Depois do CPCV: fase e escritura do comprador.
export const PurchaseInfo = ({ data, deedLine }) => (
  <div className="mt-3 space-y-1" data-testid="portal-purchase">
    <div className="text-xs uppercase tracking-wider opacity-70 font-semibold">A sua compra</div>
    <p className="text-[17px] leading-snug" data-testid="portal-purchase-phase">{data.status_label}</p>
    <p className="text-[15px] opacity-90" data-testid="portal-purchase-deed">{deedLine ? `Escritura: ${deedLine}` : "Escritura: data a confirmar"}</p>
  </div>
);

function HeroLine({ data, bform, purchase, stillMissing, deedLine, left, docsLeft }) {
  if (purchase) {
    return stillMissing.length ? (
      <p className="text-[17px] leading-snug mt-3" data-testid="portal-purchase-missing">
        {stillMissing.length === 1 ? "Ainda falta 1 documento da sua parte." : `Ainda faltam ${stillMissing.length} documentos da sua parte.`}
      </p>
    ) : <PurchaseInfo data={data} deedLine={deedLine} />;
  }
  if (bform.required && bform.done) return <p className="text-[17px] leading-snug mt-3" data-testid="portal-buyer-thanks">{thanksLine(bform.consultant_name)}</p>;
  if (left === 0 && data.progress.total > 0 && !data.deed_done_on) {
    return (
      <div className="mt-3" data-testid="portal-all-sent">
        <p className="text-[17px] leading-snug">Já enviou tudo o que precisávamos nesta fase. Obrigado!</p>
        {data.next_step && <p className="text-sm opacity-80 mt-1" data-testid="portal-next">A seguir: {data.next_step}.</p>}
      </div>
    );
  }
  if (data.stage === "angariacao") return <p className="text-[17px] leading-snug mt-3" data-testid="portal-angariacao">{`${docsLeft} para ${deal(data.negocio).goal}.`}</p>;
  if (data.deed_done_on) return <div className="text-lg font-medium mt-3" data-testid="portal-deed-done">Escritura realizada a {fmtDate(data.deed_done_on)}</div>;
  if (data.days_to_deed != null) {
    return (
      <div className="mt-3 flex items-end gap-3">
        <div className="font-mono text-5xl leading-none" data-testid="portal-days">{data.days_to_deed}</div>
        <div className="text-sm opacity-80 pb-0.5">dias até à escritura{data.deed_date ? `, ${data.deed_date}` : ""}</div>
      </div>
    );
  }
  return <div className="text-lg font-medium mt-3" data-testid="portal-deed-unconfirmed">Data da escritura a confirmar</div>;
}

// Cartão do consultor sozinho (portal): foto, nome e botões com nome.
function ConsultantCard({ c, color }) {
  const pill = "inline-flex items-center gap-2 h-10 pl-1 pr-3 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90";
  return (
    <div className="rounded-[18px] p-4 flex items-center gap-4" style={{ backgroundColor: color, color: "var(--on-dark)" }} data-testid="portal-consultant">
      {c.photo
        ? <img src={c.photo} alt={c.name} className="w-16 h-16 rounded-full object-cover border-2 border-white/60 shrink-0" data-testid="portal-consultant-photo" />
        : <span className="w-16 h-16 rounded-full bg-white/15 grid place-items-center text-2xl font-semibold shrink-0">{c.name.slice(0, 1)}</span>}
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-[0.14em] opacity-70">O seu consultor</div>
        <div className="text-[18px] font-semibold break-words leading-tight" data-testid="portal-consultant-name">{c.name}</div>
        {c.phone && (
          <div className="flex flex-wrap gap-2 mt-2">
            <a href={`https://wa.me/${waDigits(c.phone)}`} target="_blank" rel="noreferrer" className={`${pill} bg-white/10`} data-testid="portal-consultant-wa"><span className="w-8 h-8 rounded-md grid place-items-center bg-[#25D366] text-white"><SiWhatsapp size={17} /></span>WhatsApp</a>
            <a href={`tel:${c.phone.replace(/\s/g, "")}`} className={`${pill} bg-white/10`} data-testid="portal-consultant-call"><span className="w-8 h-8 rounded-md grid place-items-center bg-white text-slate-800"><Phone size={15} /></span>Ligar</a>
          </div>
        )}
      </div>
    </div>
  );
}

// Topo do portal: consultor, onde está o processo e quanto falta.
export function ProcessProgress({ data, bform, color, accent, purchase, stillMissing, deedLine }) {
  const left = data.progress.missing ?? data.progress.total - data.progress.sent;
  const docsLeft = `${left === 1 ? "Falta" : "Faltam"} ${left} ${left === 1 ? "documento" : "documentos"}`;
  return (
    <div className="space-y-4" data-testid="portal-hero">
      {data.consultant?.name && <ConsultantCard c={data.consultant} color={color} />}
      <div className="px-1 text-slate-700 [&_.opacity-80]:text-slate-500">
        <div className="font-heading text-[26px] leading-tight brand-text" data-testid="portal-hello">Olá{data.party_first_name ? `, ${data.party_first_name}` : ""}.</div>
        <div className="[&>*:first-child]:mt-1 [&_p]:text-[15px] [&_p]:text-slate-600"><HeroLine data={data} bform={bform} purchase={purchase} stillMissing={stillMissing} deedLine={deedLine} left={left} docsLeft={docsLeft} /></div>
        {data.stage !== "angariacao" && !purchase && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-full px-3 py-1" data-testid="portal-status">
            <Clock size={13} /> {data.progress.total > 0 ? `${data.progress.sent} de ${data.progress.total} enviados · ` : ""}{data.status_label}
          </div>
        )}
      </div>
    </div>
  );
}
